import { faColumns3 } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Divider } from '@material-ui/core';
import * as React from 'react';
import { useCallback } from 'react';
import { TagGroupGq } from '../../../graphql/projects/models/TagGroupGq';
import {
  ColumnsState,
  KeywordState,
  VisibleColumnsBag,
} from '../../../pages/ContentPlanPage/Components/Tables/ContentTaskTable/useVisibleColumns';
import VoxelButton from '../../voxel/Buttons/VoxelButton';
import { VoxelPopover } from '../../voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../voxel/Popover/VoxelPopoverTrigger';
import VoxelMenuItemContainer from '../../voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelDivider from '../../voxel/VoxelDivider';
import ColumnChoice from '../ColumnChoice/ColumnChoice';

export interface VisibleColumnsContentPlanPopoverProps {
  visibleColumnsBag: VisibleColumnsBag;
  tagGroups: TagGroupGq[];
}

const VisibleColumnsContentPlanPopover: React.FC<VisibleColumnsContentPlanPopoverProps> = ({
  visibleColumnsBag,
  tagGroups,
}) => {
  // Popover logic
  const createRootToggleFunction = (key: keyof ColumnsState) => () =>
    visibleColumnsBag.setState((prev) => ({ ...prev, [key]: !prev[key] }));

  const createPrimaryToggleFunction = (key: keyof KeywordState) => () =>
    visibleColumnsBag.setState((prev) => ({
      ...prev,
      primary: { ...prev.primary, [key]: !prev.primary[key] },
    }));

  const createSecondaryToggleFunction = (key: keyof KeywordState) => () =>
    visibleColumnsBag.setState((prev) => ({
      ...prev,
      secondary: { ...prev.secondary, [key]: !prev.secondary[key] },
    }));

  // Tag group logic
  const handleToggleAllTagGroups = useCallback(() => {
    visibleColumnsBag.setState((prev) => ({
      ...prev,
      shownTagGroupIds:
        tagGroups.length === prev.shownTagGroupIds.length ? [] : tagGroups.map((tg) => tg.id),
    }));
  }, [tagGroups]);

  const handleToggleTagGroup = useCallback((id: number) => {
    visibleColumnsBag.setState((prev) => {
      if (prev.shownTagGroupIds.some((tagGroupId) => tagGroupId === id)) {
        return { ...prev, shownTagGroupIds: prev.shownTagGroupIds.filter((tg) => tg !== id) };
      }
      return { ...prev, shownTagGroupIds: [...prev.shownTagGroupIds, id] };
    });
  }, []);

  return (
    <VoxelPopover>
      <VoxelPopoverTrigger enableActive>
        <VoxelButton
          size={'md'}
          startIcon={<FontAwesomeIcon icon={faColumns3} />}
          title={'Columns'}
          color="light-gray"
        />
      </VoxelPopoverTrigger>
      <VoxelPopoverContent>
        <VoxelMenuItemContainer>
          <ColumnChoice
            toggle={createRootToggleFunction('approved')}
            visible={visibleColumnsBag.state.approved}
            title={'Status'}
          />
          <ColumnChoice
            toggle={createRootToggleFunction('action')}
            visible={visibleColumnsBag.state.action}
            title={'Action'}
          />

          <ColumnChoice
            toggle={createRootToggleFunction('url')}
            visible={visibleColumnsBag.state.url}
            title={'URL'}
          />

          <ColumnChoice
            toggle={createRootToggleFunction('statusCode')}
            visible={visibleColumnsBag.state.statusCode}
            title={'HTTP'}
          />
          <ColumnChoice
            toggle={createRootToggleFunction('chatGpt')}
            visible={visibleColumnsBag.state.chatGpt}
            title={'ChatGPT'}
          />

          <ColumnChoice
            toggle={createRootToggleFunction('totalVolume')}
            visible={visibleColumnsBag.state.totalVolume}
            title={'Total volume'}
          />
          <ColumnChoice
            toggle={createRootToggleFunction('totalTraffic')}
            visible={visibleColumnsBag.state.totalTraffic}
            title={'Total traffic'}
          />
        </VoxelMenuItemContainer>
        <VoxelDivider />
        <VoxelMenuItemContainer>
          <ColumnChoice
            toggle={() =>
              visibleColumnsBag.setState((prev) => {
                const next = { ...prev, primary: { ...prev.primary } };
                const allChecked = Object.values(prev.primary).every((v) => v);
                Object.keys(prev.primary).forEach((key: keyof KeywordState) => {
                  next.primary[key] = !allChecked;
                });

                return next;
              })
            }
            visible={Object.values(visibleColumnsBag.state.primary).every((v) => v)}
            title="Primary"
          />
        </VoxelMenuItemContainer>
        <VoxelDivider />
        <VoxelMenuItemContainer>
          <ColumnChoice
            toggle={createPrimaryToggleFunction('keyword')}
            visible={visibleColumnsBag.state.primary.keyword}
            title={'Keyword'}
          />

          <ColumnChoice
            toggle={createPrimaryToggleFunction('inTitle')}
            visible={visibleColumnsBag.state.primary.inTitle}
            title={'In title'}
          />

          <ColumnChoice
            toggle={createPrimaryToggleFunction('inh1')}
            visible={visibleColumnsBag.state.primary.inh1}
            title={'in H1'}
          />

          <ColumnChoice
            toggle={createPrimaryToggleFunction('inContent')}
            visible={visibleColumnsBag.state.primary.inContent}
            title={'in content'}
          />

          <ColumnChoice
            toggle={createPrimaryToggleFunction('rank')}
            visible={visibleColumnsBag.state.primary.rank}
            title={'Rank'}
          />

          <ColumnChoice
            toggle={createPrimaryToggleFunction('volume')}
            visible={visibleColumnsBag.state.primary.volume}
            title={'Volume'}
          />

          <ColumnChoice
            toggle={createPrimaryToggleFunction('traffic')}
            visible={visibleColumnsBag.state.primary.traffic}
            title={'Traffic'}
          />
        </VoxelMenuItemContainer>
        <VoxelDivider />
        <VoxelMenuItemContainer>
          <ColumnChoice
            toggle={() =>
              visibleColumnsBag.setState((prev) => {
                const next = { ...prev, secondary: { ...prev.secondary } };

                const allChecked = Object.values(prev.secondary).every((v) => v);
                Object.keys(prev.secondary).forEach((key: keyof KeywordState) => {
                  next.secondary[key] = !allChecked;
                });

                return next;
              })
            }
            visible={Object.values(visibleColumnsBag.state.secondary).every((v) => v)}
            title="Secondary"
          />
        </VoxelMenuItemContainer>
        <VoxelDivider />
        <VoxelMenuItemContainer>
          <ColumnChoice
            toggle={createSecondaryToggleFunction('keyword')}
            visible={visibleColumnsBag.state.secondary.keyword}
            title={'Keyword'}
          />

          <ColumnChoice
            toggle={createSecondaryToggleFunction('inTitle')}
            visible={visibleColumnsBag.state.secondary.inTitle}
            title={'In title'}
          />

          <ColumnChoice
            toggle={createSecondaryToggleFunction('inh1')}
            visible={visibleColumnsBag.state.secondary.inh1}
            title={'in H1'}
          />

          <ColumnChoice
            toggle={createSecondaryToggleFunction('inContent')}
            visible={visibleColumnsBag.state.secondary.inContent}
            title={'in content'}
          />

          <ColumnChoice
            toggle={createSecondaryToggleFunction('rank')}
            visible={visibleColumnsBag.state.secondary.rank}
            title={'Rank'}
          />

          <ColumnChoice
            toggle={createSecondaryToggleFunction('volume')}
            visible={visibleColumnsBag.state.secondary.volume}
            title={'Volume'}
          />

          <ColumnChoice
            toggle={createSecondaryToggleFunction('traffic')}
            visible={visibleColumnsBag.state.secondary.traffic}
            title={'Traffic'}
          />

          <ColumnChoice
            toggle={createRootToggleFunction('longTailKeywords')}
            visible={visibleColumnsBag.state.longTailKeywords}
            title={'Long tail keywords'}
          />

          {!!tagGroups.length && (
            <>
              <Divider />
              <ColumnChoice
                toggle={handleToggleAllTagGroups}
                visible={!!visibleColumnsBag.state.shownTagGroupIds.length}
                title={'All tag groups'}
              />
              {tagGroups.map((tg) => (
                <ColumnChoice
                  key={tg.id}
                  toggle={() => handleToggleTagGroup(tg.id)}
                  visible={visibleColumnsBag.state.shownTagGroupIds.includes(tg.id)}
                  title={tg.name}
                />
              ))}
            </>
          )}
        </VoxelMenuItemContainer>
      </VoxelPopoverContent>
    </VoxelPopover>
  );
};

export default VisibleColumnsContentPlanPopover;
